import { instance } from '../../axios/AxiosInstance';
import { putFileToS3, requestPresignedPutUrl } from '../Settings/lists/categoryApi';
import type { ListResponse } from '../Settings/lists/singleTableListSupport';
import { defaultPageLangContent } from './bookForm/model';
import type { BookFormDraft, BookPageLangContent } from './bookForm/types';

const PATH = 'books';

/** Matches `BookLanguageEntryStored` from the books API. */
export interface BookLanguageEntryApi {
  lang: string;
  title: string;
  description: string;
  pages: unknown[];
}

/** Matches `BookDynamoItem` from the books API list/get response. */
export interface BookListItem {
  id: string;
  author_id: number;
  cover_image_path: string;
  promo_image_path: string;
  age_groups: number[];
  categories: number[];
  language_map: Record<string, BookLanguageEntryApi>;
  writer_emails?: string[];
  version: string;
  published: boolean;
  published_by: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export async function fetchBooks(): Promise<BookListItem[]> {
  const { data } = await instance.get<ListResponse>(PATH);
  return Array.isArray(data.data) ? (data.data as BookListItem[]) : [];
}

export async function fetchBook(id: string): Promise<BookListItem> {
  const trimmed = id.trim();
  if (!trimmed) {
    throw new Error('Номын ID хоосон байна.');
  }
  const { data } = await instance.get<{ data?: BookListItem }>(PATH, { params: { id: trimmed } });
  const item = data?.data;
  if (item == null || typeof item !== 'object') {
    throw new Error('Ном олдсонгүй.');
  }
  return item as BookListItem;
}

export async function updateBook(body: object): Promise<void> {
  await instance.put(PATH, body);
}

export async function deleteBook(id: string): Promise<void> {
  const trimmed = id.trim();
  if (!trimmed) {
    throw new Error('Номын ID хоосон байна.');
  }
  await instance.delete(PATH, { data: { id: trimmed } });
}

/** PUT `/books/publish` — manager only. */
export async function publishBook(id: string, published: boolean): Promise<void> {
  const trimmed = id.trim();
  if (!trimmed) {
    throw new Error('Номын ID хоосон байна.');
  }
  await instance.put(`${PATH}/publish`, { id: trimmed, published });
}

type ApiPageRow = {
  page_number: number;
  image_path: string;
  video_path: string;
  full_text: string;
};

function pagesFromLangEntry(pagesRaw: unknown): ApiPageRow[] {
  if (!Array.isArray(pagesRaw)) return [];
  const out: ApiPageRow[] = [];
  for (const raw of pagesRaw) {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const o = raw as Record<string, unknown>;
    const n = typeof o.page_number === 'number' ? o.page_number : parseInt(String(o.page_number ?? ''), 10);
    if (!Number.isFinite(n)) continue;
    out.push({
      page_number: Math.floor(n),
      image_path: o.image_path != null ? String(o.image_path) : '',
      video_path: o.video_path != null ? String(o.video_path) : '',
      full_text: o.full_text != null ? String(o.full_text) : '',
    });
  }
  return out.sort((a, b) => a.page_number - b.page_number);
}

function normalizeWriterEmailLocal(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Map GET `/books?id=` item into the shared book form draft (edit mode). */
export function apiBookToFormDraft(book: BookListItem): BookFormDraft {
  const langKeys = Object.keys(book.language_map ?? {}).filter((k) => k.trim());
  const language_enabled: Record<string, boolean> = {};
  for (const k of langKeys) {
    language_enabled[k] = true;
  }

  const name_map: Record<string, string> = {};
  const description_map: Record<string, string> = {};
  for (const langId of langKeys) {
    const e = book.language_map[langId];
    name_map[langId] = e?.title != null ? String(e.title) : '';
    description_map[langId] = e?.description != null ? String(e.description) : '';
  }

  const pageNumSet = new Set<number>();
  for (const langId of langKeys) {
    for (const p of pagesFromLangEntry(book.language_map[langId]?.pages)) {
      pageNumSet.add(p.page_number);
    }
  }
  const pageNumbers = Array.from(pageNumSet).sort((a, b) => a - b);

  const pages = pageNumbers.map((pn) => {
    const byLang: Record<string, BookPageLangContent> = {};
    let image_path = '';
    let video_path = '';
    for (const langId of langKeys) {
      const list = pagesFromLangEntry(book.language_map[langId]?.pages);
      const found = list.find((p) => p.page_number === pn);
      if (found) {
        byLang[langId] = { full_text: found.full_text };
        if (!image_path.trim()) image_path = found.image_path;
        if (!video_path.trim()) video_path = found.video_path;
      } else {
        byLang[langId] = defaultPageLangContent();
      }
    }
    return {
      clientId: crypto.randomUUID(),
      page_number: pn,
      image_path,
      video_path,
      image_file: null,
      byLang,
    };
  });

  return {
    mode: 'edit',
    id: book.id,
    author_id: String(book.author_id),
    category_ids: book.categories.map((n) => String(n)),
    age_group_ids: book.age_groups.map((n) => String(n)),
    writer_emails: Array.isArray(book.writer_emails)
      ? Array.from(
          new Set(
            book.writer_emails
              .map((e) => normalizeWriterEmailLocal(String(e ?? '')))
              .filter((e) => e.length > 0),
          ),
        )
      : [],
    language_enabled,
    name_map,
    description_map,
    cover_image_path: book.cover_image_path != null ? String(book.cover_image_path) : '',
    promo_image_path: book.promo_image_path != null ? String(book.promo_image_path) : '',
    cover_image_file: null,
    promo_image_file: null,
    pages,
    published: Boolean(book.published),
  };
}

function contentTypeForImageKey(key: string): string {
  const lower = key.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.webp')) return 'image/webp';
  return 'image/jpeg';
}

function buildBookImageKey(folderId: string, kind: 'cover' | 'promo', file: File): string {
  const safe = folderId.replace(/[^a-zA-Z0-9_-]/g, '') || 'draft';
  const raw = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^\.+/, '') || 'image';
  return `book/${safe}/${kind}-${raw}`;
}

function buildBookPageImageKey(folderId: string, pageClientId: string, file: File): string {
  const safeFolder = folderId.replace(/[^a-zA-Z0-9_-]/g, '') || 'draft';
  const safePage = pageClientId.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 24) || 'p';
  const raw = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^\.+/, '') || 'image';
  return `book/${safeFolder}/page-${safePage}-${raw}`;
}

/** Upload cover/promo before POST `/books` when the user picked a file but has no S3 key yet. */
export async function uploadBookImageFile(folderId: string, kind: 'cover' | 'promo', file: File): Promise<string> {
  const key = buildBookImageKey(folderId, kind, file);
  const contentType = contentTypeForImageKey(key);
  const uploadUrl = await requestPresignedPutUrl(key);
  await putFileToS3(uploadUrl, file, contentType);
  return key;
}

export async function uploadBookPageImageFile(folderId: string, pageClientId: string, file: File): Promise<string> {
  const key = buildBookPageImageKey(folderId, pageClientId, file);
  const contentType = contentTypeForImageKey(key);
  const uploadUrl = await requestPresignedPutUrl(key);
  await putFileToS3(uploadUrl, file, contentType);
  return key;
}

/** Upload any cover/promo/page files so `cover_image_path` and page `image_path` values are set for POST `/books`. */
export async function prepareBookDraftForApiUpload(draft: BookFormDraft): Promise<BookFormDraft> {
  const folderId = crypto.randomUUID();
  let next: BookFormDraft = {
    ...draft,
    pages: draft.pages.map((p) => ({
      ...p,
      byLang: Object.fromEntries(Object.entries(p.byLang).map(([k, v]) => [k, { ...v }])),
    })),
  };

  if (!next.cover_image_path.trim() && next.cover_image_file) {
    next = {
      ...next,
      cover_image_path: await uploadBookImageFile(folderId, 'cover', next.cover_image_file),
      cover_image_file: null,
    };
  }
  if (!next.promo_image_path.trim() && next.promo_image_file) {
    next = {
      ...next,
      promo_image_path: await uploadBookImageFile(folderId, 'promo', next.promo_image_file),
      promo_image_file: null,
    };
  }

  const pages = await Promise.all(
    next.pages.map(async (page) => {
      if (!page.image_path.trim() && page.image_file) {
        const key = await uploadBookPageImageFile(folderId, page.clientId, page.image_file);
        return { ...page, image_path: key, image_file: null };
      }
      return page;
    }),
  );
  return { ...next, pages };
}

export async function createBook(body: object): Promise<{ id: string }> {
  const { data } = await instance.post<{ message?: string; data?: { id?: string } }>(PATH, body);
  const id = data?.data?.id;
  if (id == null || String(id).trim() === '') {
    throw new Error('Ном үүсгэхэд ID ирсэнгүй.');
  }
  return { id: String(id) };
}
